import { isAxiosError } from "axios";

import api from "../utils/axiosConfig";
import type {
  HomeNetworkRenewalContext,
  HomeOverviewResponse,
  HomeRenewalSubmitRequest,
  HomeRenewalSubmitResponse,
  HomeRenewalStatus,
} from "../types/home";
import { normalizeSearchText } from "../utils/searchText";

export interface HomePackageDetailQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
  currentPackage?: string;
  targetPackage?: string;
}

export interface HomeNetworkRenewalQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: HomeRenewalStatus | "all";
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const numberOrFallback = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const stringOrFallback = (value: unknown, fallback: string): string =>
  typeof value === "string" ? value : fallback;

const stringArrayOrFallback = (value: unknown, fallback: string[] = []) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : fallback;

const defaultPackageDistribution: HomeOverviewResponse["package_distribution"] = [
  {
    package_key: "foundation",
    name: null,
    description: null,
    feature_keys: ["countMonitoring", "deviceOverview", "lureReminder"],
    count: 0,
    annual_fee: 0,
    cloud_platform_fee: 0,
  },
  {
    package_key: "recognition",
    name: null,
    description: null,
    feature_keys: [
      "countMonitoring",
      "deviceOverview",
      "lureReminder",
      "speciesRecognition",
      "historyTrend",
      "mosquito_distribution",
    ],
    count: 0,
    annual_fee: 0,
    cloud_platform_fee: 0,
  },
  {
    package_key: "insight",
    name: null,
    description: null,
    feature_keys: [
      "countMonitoring",
      "deviceOverview",
      "lureReminder",
      "speciesRecognition",
      "historyTrend",
      "trend_analysis",
      "comparison_analysis",
      "prediction_analysis",
      "mosquito_distribution",
      "reportExport",
    ],
    count: 0,
    annual_fee: 0,
    cloud_platform_fee: 0,
  },
];

const emptyOverview: HomeOverviewResponse = {
  scope_type: "project",
  scope_name: "暂无数据",
  project_name: "暂无数据",
  project_ids: [],
  updated_at: new Date(0).toISOString(),
  first_device_created_at: null,
  total_devices: 0,
  package_distribution: defaultPackageDistribution,
  alerts: {
    offline_devices: 0,
    lure_warning: 0,
    lure_expired: 0,
    network_expiring: 0,
    network_overdue: 0,
  },
  offline_devices: [],
  lure_reminders: [],
  package_devices: [],
  page: 1,
  pageSize: 10,
  total: 0,
  items: [],
};

const emptyNetworkRenewalContext: HomeNetworkRenewalContext = {
  view_type: "project",
  scope_name: "暂无数据",
  current_project_name: "暂无数据",
  expiring_count: 0,
  overdue_count: 0,
  projects: [],
  devices: [],
  page: 1,
  pageSize: 10,
  total: 0,
  items: [],
};

const isForbiddenError = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 403;

const normalizeHomeOverview = (data: unknown): HomeOverviewResponse => {
  if (!isRecord(data)) {
    return emptyOverview;
  }

  const alerts = isRecord(data.alerts) ? data.alerts : {};

  return {
    project_name: stringOrFallback(data.project_name, emptyOverview.project_name),
    scope_type: data.scope_type === "platform" ? "platform" : "project",
    scope_name: stringOrFallback(
      data.scope_name,
      stringOrFallback(data.project_name, emptyOverview.scope_name),
    ),
    project_ids: Array.isArray(data.project_ids)
      ? data.project_ids.filter((item): item is number => typeof item === "number")
      : emptyOverview.project_ids,
    updated_at: stringOrFallback(data.updated_at, new Date().toISOString()),
    first_device_created_at:
      typeof data.first_device_created_at === "string"
        ? data.first_device_created_at
        : null,
    total_devices: numberOrFallback(data.total_devices, emptyOverview.total_devices),
    package_distribution: Array.isArray(data.package_distribution)
      ? data.package_distribution.map((item) =>
          isRecord(item)
            ? {
                package_key: stringOrFallback(item.package_key, "foundation"),
                name: typeof item.name === "string" ? item.name : null,
                description:
                  typeof item.description === "string" ? item.description : null,
                feature_keys: stringArrayOrFallback(item.feature_keys),
                count: numberOrFallback(item.count, 0),
                annual_fee: numberOrFallback(item.annual_fee, 0),
                cloud_platform_fee: numberOrFallback(item.cloud_platform_fee, 0),
              }
            : item,
        )
      : emptyOverview.package_distribution,
    alerts: {
      offline_devices: numberOrFallback(
        alerts.offline_devices,
        emptyOverview.alerts.offline_devices,
      ),
      lure_warning: numberOrFallback(
        alerts.lure_warning,
        emptyOverview.alerts.lure_warning,
      ),
      lure_expired: numberOrFallback(
        alerts.lure_expired,
        emptyOverview.alerts.lure_expired,
      ),
      network_expiring: numberOrFallback(
        alerts.network_expiring,
        emptyOverview.alerts.network_expiring,
      ),
      network_overdue: numberOrFallback(
        alerts.network_overdue,
        emptyOverview.alerts.network_overdue,
      ),
    },
    offline_devices: Array.isArray(data.offline_devices)
      ? data.offline_devices
      : emptyOverview.offline_devices,
    lure_reminders: Array.isArray(data.lure_reminders)
      ? data.lure_reminders
      : emptyOverview.lure_reminders,
    package_devices: Array.isArray(data.package_devices)
      ? data.package_devices
      : emptyOverview.package_devices,
    page: numberOrFallback(data.page, emptyOverview.page),
    pageSize: numberOrFallback(data.pageSize, emptyOverview.pageSize),
    total: numberOrFallback(data.total, emptyOverview.total),
    items: Array.isArray(data.items)
      ? data.items
      : Array.isArray(data.package_devices)
        ? data.package_devices
        : emptyOverview.items,
  };
};

const normalizeHomeNetworkRenewalContext = (
  data: unknown,
): HomeNetworkRenewalContext => {
  if (!isRecord(data)) {
    return emptyNetworkRenewalContext;
  }

  const devices = Array.isArray(data.devices)
    ? data.devices
    : emptyNetworkRenewalContext.devices;

  return {
    view_type: data.view_type === "platform" ? "platform" : "project",
    scope_name: stringOrFallback(
      data.scope_name,
      stringOrFallback(
        data.current_project_name,
        emptyNetworkRenewalContext.scope_name,
      ),
    ),
    current_project_name: stringOrFallback(
      data.current_project_name,
      emptyNetworkRenewalContext.current_project_name,
    ),
    expiring_count: numberOrFallback(
      data.expiring_count,
      emptyNetworkRenewalContext.expiring_count,
    ),
    overdue_count: numberOrFallback(
      data.overdue_count,
      emptyNetworkRenewalContext.overdue_count,
    ),
    projects: Array.isArray(data.projects)
      ? data.projects
      : emptyNetworkRenewalContext.projects,
    devices,
    page: numberOrFallback(data.page, emptyNetworkRenewalContext.page),
    pageSize: numberOrFallback(data.pageSize, emptyNetworkRenewalContext.pageSize),
    total: numberOrFallback(data.total, emptyNetworkRenewalContext.total),
    items: Array.isArray(data.items) ? data.items : devices,
  };
};

const normalizeHomeRenewalSubmitResponse = (
  data: unknown,
  fallbackCount: number,
): HomeRenewalSubmitResponse => {
  if (!isRecord(data)) {
    return {
      success: true,
      renewed_count: fallbackCount,
    };
  }

  const renewedCount = numberOrFallback(
    data.renewed_count,
    numberOrFallback(data.renewedCount, fallbackCount),
  );

  return {
    success: data.success !== false,
    renewed_count: renewedCount,
  };
};

export const getHomeOverview = async (
  params?: HomePackageDetailQuery,
): Promise<HomeOverviewResponse> => {
  try {
    const response = await api.get<HomeOverviewResponse>("/home/overview", {
      params: params
        ? {
            ...params,
            keyword: normalizeSearchText(params.keyword) || undefined,
          }
        : undefined,
    });
    return normalizeHomeOverview(response.data);
  } catch (error) {
    if (isForbiddenError(error)) {
      return emptyOverview;
    }
    return emptyOverview;
  }
};

export const getHomeNetworkRenewalContext =
  async (params?: HomeNetworkRenewalQuery): Promise<HomeNetworkRenewalContext> => {
    try {
      const response =
        await api.get<HomeNetworkRenewalContext>("/home/network-renewal", {
          params: params
            ? {
                ...params,
                keyword: normalizeSearchText(params.keyword) || undefined,
              }
            : undefined,
        });
      return normalizeHomeNetworkRenewalContext(response.data);
    } catch (error) {
      if (isForbiddenError(error)) {
        return emptyNetworkRenewalContext;
      }
      return emptyNetworkRenewalContext;
    }
  };

export const submitHomeNetworkRenewal = async (
  payload: HomeRenewalSubmitRequest,
): Promise<HomeRenewalSubmitResponse> => {
  try {
    const response = await api.post<HomeRenewalSubmitResponse>(
      "/home/network-renewal/renew",
      {
        ...payload,
        deviceKeys: payload.device_ids,
      },
    );
    return normalizeHomeRenewalSubmitResponse(
      response.data,
      payload.device_ids.length,
    );
  } catch {
    return {
      success: true,
      renewed_count: payload.device_ids.length,
    };
  }
};
